import { useLocation } from "@tanstack/react-router";
import * as React from "react";

import { findActiveParent, NavSubView } from "./components/nav-group";
import { NavMain } from "./components/nav-main";
import { NavProjects } from "./components/nav-projects";
import { NavSettings } from "./components/nav-settings";
import { NavUser } from "./components/nav-user";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { findNav, primaryNav, settingsNav, tertiaryNav, type NavEntry } from "@/constants/nav.constants";
import { BrandMark } from "@/features/site/components/brand-mark";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { appConfig } from "@complete-athlete/enums";

// Wider than the default 16rem sidebar so the nav labels and section titles get
// room to breathe against the frosted glass surface. Applied on the
// SidebarProvider (see the page layout) so the layout gap tracks the same width
// as the panel. Setting it only on the Sidebar leaves the gap at the default
// width, which is why width tweaks looked like they did nothing.
export const SIDEBAR_WIDTH = "18rem";

// The icon (collapsed) sidebar is wider than the shared default 3rem so the
// circular icon pills get horizontal breathing room instead of sitting cramped
// against the edges. Applied on the SidebarProvider (see the page layout) via
// the --sidebar-width-icon var, which the sidebar primitives read for the
// collapsed width.
export const SIDEBAR_WIDTH_ICON = "3.75rem";

// Where "Back to menu" lands: the first top-level leaf (a primary entry without
// children), falling back to the first primary entry, then "/". This brings the
// sidebar back to the main menu view by leaving the drilled route space.
function backToRoute(entries: NavEntry[]): string {
  const firstLeaf = entries.find((entry) => entry.items.length === 0);
  if (firstLeaf && "to" in firstLeaf) return firstLeaf.to;
  const first = entries[0];
  if (first && "to" in first) return first.to;
  return "/";
}

type DrillDirection = "forward" | "back";

/**
 * Wraps a nav "stage" (main menu or a drilled sub-view) and slides it in to make
 * the depth change legible. Going deeper slides in from the right; going back
 * slides in from the left. Keying the component itself (done at the call site
 * with `stageKey`) remounts it per view so each stage starts at the offset and
 * animates to rest.
 */
function NavStage({ direction, children }: { direction: DrillDirection; children: React.ReactNode }) {
  const [entered, setEntered] = React.useState(false);

  React.useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div
      className={cn(
        // Scope the transition to the drill slide's transform + opacity only.
        // `transition-all` would also animate this wrapper's width/padding during
        // a sidebar collapse, lagging behind the panel's own 200ms width
        // transition and making the nav items rush/slides into place as they
        // track the shrinking edge late.
        "transition-[transform,opacity] duration-200 ease-out",
        entered
          ? "translate-x-0 opacity-100"
          : direction === "forward"
            ? "translate-x-6 opacity-0"
            : "-translate-x-6 opacity-0",
      )}
    >
      {children}
    </div>
  );
}

export function AppSidebar(props: React.ComponentProps<typeof Sidebar>) {
  const { pathname } = useLocation();
  // Only used for the header toggle's a11y attributes. The collapsed styling
  // itself is CSS (`group-data-[collapsible=icon]:`) so it animates in both
  // directions; the mobile sheet never reports a collapsed state.
  const { state, isMobile } = useSidebar();
  const isCollapsed = !isMobile && state === "collapsed";

  // The drilled sub-view is driven by the URL: when the current route lives
  // inside a parent's route space, the sidebar shows that parent's children
  // instead of the main menu. Works on direct navigation and refresh, not just
  // on clicks.
  const allEntries = React.useMemo<NavEntry[]>(() => [...primaryNav, ...findNav, ...tertiaryNav, ...settingsNav], []);
  const activeParent = React.useMemo(() => findActiveParent(allEntries, pathname), [allEntries, pathname]);
  const isDrilled = activeParent !== null;
  const backTo = React.useMemo(() => backToRoute(primaryNav), []);

  // Track drill direction across renders so the stage animation can slide the
  // right way. Updated during render (refs only) so the incoming view picks up
  // the correct direction immediately.
  const prevDrilledRef = React.useRef(isDrilled);
  const directionRef = React.useRef<DrillDirection>("forward");
  if (prevDrilledRef.current !== isDrilled) {
    directionRef.current = isDrilled ? "forward" : "back";
    prevDrilledRef.current = isDrilled;
  }
  const direction = directionRef.current;
  const stageKey = isDrilled && activeParent ? `parent:${activeParent.label}` : "root";

  return (
    <Sidebar
      collapsible="icon"
      // Frosted glass: the sidebar surface is translucent so the page-wide flairs
      // bleed through, and a heavy backdrop blur diffuses them into the soft glow
      // behind the nav. The default sidebar adds a right border (a divider line);
      // kill it so the glass sidebar sits flush against the solid content with no
      // seam between them. Targets the inner panel for the glass surface.
      className="[&_[data-sidebar=sidebar]]:bg-transparent! [&_[data-sidebar=rail]]:hidden! !border-0"
      {...props}
    >
      <SidebarHeader>
        {/* One row in both states, and nothing in it ever changes its flex
          direction or alignment. Collapsing only eases the row's own inset from
          20px down to the 6px that leaves a 32px logo centered in the rail (the
          sidebar header's own p-2 makes up the rest), so the logo glides 14px
          left in lockstep with the panel instead of the slot folding away.
          `linear` + 200ms match the panel width transition exactly.
          Everything that cannot fit the rail fades on the spot and is clipped by
          the row, so nothing is pushed sideways on its way out. */}
        <div className="relative flex h-auto flex-nowrap items-center gap-2.5 overflow-hidden px-5 pt-3 transition-[padding] duration-200 ease-linear group-data-[collapsible=icon]:px-1.5">
          {/* `shrink-0`, not `flex-1`: once the rail is narrower than the branding,
            a shrinking slot would hit 0 width and squash the mark. Keeping the
            slot at its content width lets it overflow into the row's clip instead. */}
          <div className="flex h-8 shrink-0 items-center gap-2.5">
            <BrandMark className="size-8 shrink-0 rounded-[11px]" />
            <span className="font-semibold text-lg whitespace-nowrap text-foreground transition-opacity duration-100 ease-out group-data-[collapsible=icon]:opacity-0">
              {appConfig.name}
            </span>
          </div>
          {/* The rail is only wide enough for the logo, so the toggle hands off
            to the copy sitting next to the breadcrumbs (see the page layout)
            rather than staying here and shoving the logo around. It fades in
            place and stays in flow: pulling it from the DOM would resize the row
            mid-transition. `tabIndex`/`aria-hidden` follow the fade so a
            faded-out button is not focusable. */}
          <SidebarTrigger
            tabIndex={isCollapsed ? -1 : undefined}
            aria-hidden={isCollapsed}
            className="ml-auto -mr-2 shrink-0 text-muted-foreground transition-opacity duration-150 ease-linear hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground dark:hover:bg-sidebar-foreground/10 group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0"
          />
        </div>
      </SidebarHeader>
      <SidebarContent className="gap-1 overflow-x-hidden h-full">
        <NavStage key={stageKey} direction={direction}>
          {isDrilled && activeParent ? (
            <NavSubView
              label={activeParent.label}
              items={activeParent.items}
              backTo={backTo}
              backLabel={m.layout_sidebar_back_to_menu()}
            />
          ) : (
            <NavMain />
          )}
        </NavStage>
        {/* Lower-priority groups (settings + the tertiary "Workspace" rail that
          holds Settings/Billing/etc.) pin to the bottom above the account.
          Always rendered, including while drilled into a primary sub-view. */}
        <div className="mt-auto">
          <NavSettings />
          <NavProjects />
        </div>
      </SidebarContent>
      {/* px-3 when collapsed for the same reason as the nav groups: it is what
        leaves the 2.25rem account row centered in the rail with nothing centering
        it. The label collapses by margin instead of `hidden` so the avatar rises
        over the same 200ms as everything else rather than jumping the moment the
        toggle is pressed. */}
      <SidebarFooter className="gap-1 px-5 pb-5 pt-3 transition-[padding] duration-200 ease-linear group-data-[collapsible=icon]:px-3">
        <div className="flex h-4 items-center px-3 text-[11px] font-medium tracking-wider text-sidebar-foreground/50 transition-[margin,opacity] duration-200 ease-linear group-data-[collapsible=icon]:-mt-5 group-data-[collapsible=icon]:opacity-0">
          {m.layout_sidebar_account()}
        </div>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
